<template>
  <div class="max-w-4xl mx-auto">
    <Head title="Editar producto" />
    
    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Link href="/products" class="text-gray-400 hover:text-orange-500 transition-colors font-bold">
            Productos
          </Link>
          <span class="text-gray-300 font-light">/</span>
          <span>{{ form.name }}</span>
          <span class="px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-100 ml-1">{{ form.code }}</span>
        </h1>
        <p class="text-sm text-gray-500 mt-0.5">Actualice la información y precios del producto.</p>
      </div>
    </div>

    <!-- Form -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <form @submit.prevent="update">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
          <!-- Basic Info -->
          <div class="md:col-span-2">
            <h2 class="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i class="fa fa-info-circle text-orange-500" />
              Información Básica
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Code -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Código *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-barcode text-gray-400" />
                  </div>
                  <input 
                    v-model="form.code"
                    type="text" 
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.code}"
                    placeholder="Ingrese el código del producto"
                  />
                </div>
                <p v-if="form.errors.code" class="text-red-500 text-xs mt-1">{{ form.errors.code }}</p>
              </div>

              <!-- Name -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Nombre *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-box text-gray-400" />
                  </div>
                  <input 
                    v-model="form.name"
                    type="text" 
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.name}"
                    placeholder="Ingrese el nombre del producto"
                  />
                </div>
                <p v-if="form.errors.name" class="text-red-500 text-xs mt-1">{{ form.errors.name }}</p>
              </div>

              <!-- Type -->
              <div class="space-y-1.5 md:col-span-2">
                <label class="block text-sm font-medium text-gray-700">Tipo de producto *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-shapes text-gray-400" />
                  </div>
                  <select 
                    v-model="form.type_product"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.type_product}"
                  >
                    <option value="">Seleccionar tipo</option>
                    <option value="product">Producto Estándar</option>
                    <option value="service">Servicio</option>
                    <option value="insumo">Insumo / Ingrediente</option>
                    <option value="recipe">Elaborado / Receta</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.type_product" class="text-red-500 text-xs mt-1">{{ form.errors.type_product }}</p>
              </div>

              <!-- Insumo Helper Notice -->
              <div v-if="form.type_product === 'insumo'" class="md:col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                <div class="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i class="fa fa-boxes-stacked" />
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-amber-900">Insumo / Materia Prima</h4>
                  <p class="text-xs text-amber-700 mt-0.5">
                    Este producto no se ofrecerá directamente en canales de venta (POS, Ventas regulares ni Catálogo). Se gestionará su inventario en Compras/Inventarios y se consumirá automáticamente al vender los productos Elaborados/Recetas que lo contengan.
                  </p>
                </div>
              </div>

              <!-- Recipe Builder -->
              <div v-if="form.type_product === 'recipe'" class="md:col-span-2">
                <recipe-builder
                  :items="form.recipe_items"
                  :insumos="insumos"
                  :available-ingredients="availableIngredients"
                  @update:items="form.recipe_items = $event"
                  @cost-changed="onRecipeCostChanged"
                />
                <p v-if="form.errors.recipe_items" class="text-red-500 text-xs mt-1">{{ form.errors.recipe_items }}</p>
              </div>

              <!-- Description -->
              <div class="space-y-1.5 md:col-span-2">
                <label class="block text-sm font-medium text-gray-700">Descripción</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 pt-3 pointer-events-none">
                    <i class="fa fa-align-left text-gray-400" />
                  </div>
                  <textarea 
                    v-model="form.description"
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.description}"
                    placeholder="Ingrese una descripción"
                    rows="3"
                  />
                </div>
                <p v-if="form.errors.description" class="text-red-500 text-xs mt-1">{{ form.errors.description }}</p>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-100 col-span-1 md:col-span-2 my-2" />

          <!-- Prices -->
          <div class="md:col-span-2">
            <h2 class="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i class="fa fa-tag text-orange-500" />
              Precios y Costos
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Cost -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Costo *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-money-bill text-gray-400" />
                  </div>
                  <input 
                    v-model="form.cost"
                    type="number" 
                    min="0"
                    step="0.01"
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="{'border-red-300': form.errors.cost}"
                    placeholder="0.00"
                  />
                </div>
                <p v-if="form.errors.cost" class="text-red-500 text-xs mt-1">{{ form.errors.cost }}</p>
              </div>

              <!-- Dynamic Prices -->
              <div v-for="(group, key) in groups" :key="key" class="space-y-1.5">
                <div class="flex justify-between items-center">
                  <label class="block text-sm font-medium text-gray-700">Precio {{ group.name }} *</label>
                  <span v-if="form.cost > 0 && form.prices[key] > 0 && Number(form.prices[key]) < Number(form.cost)" class="text-xs text-red-500 font-medium flex items-center gap-1">
                    <i class="fa fa-exclamation-triangle" /> Menor al costo
                  </span>
                </div>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-tag text-gray-400" />
                  </div>
                  <input 
                    v-model="form.prices[key]"
                    type="number" 
                    min="0.01"
                    step="0.01"
                    class="block w-full pl-10 pr-3 py-2.5 bg-white border rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                    :class="(form.errors['prices.' + key] || form.errors.prices || (form.cost > 0 && form.prices[key] > 0 && Number(form.prices[key]) < Number(form.cost))) ? 'border-red-500 bg-red-50/20' : 'border-gray-200'"
                    placeholder="0.00"
                  />
                </div>
                <p v-if="form.errors['prices.' + key]" class="text-red-500 text-xs mt-1">{{ form.errors['prices.' + key] }}</p>
                <p v-else-if="form.errors.prices" class="text-red-500 text-xs mt-1">{{ form.errors.prices }}</p>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-100 col-span-1 md:col-span-2 my-2" />

          <!-- Categories -->
          <div class="md:col-span-2">
            <h2 class="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i class="fa fa-layer-group text-orange-500" />
              Categorización
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Brand -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Marca *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-bookmark text-gray-400" />
                  </div>
                  <select 
                    v-model="form.brand_id"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.brand_id}"
                  >
                    <option value="">Seleccionar marca</option>
                    <option v-for="brand in brands" :key="brand.id" :value="brand.id">
                      {{ brand.name }}
                    </option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.brand_id" class="text-red-500 text-xs mt-1">{{ form.errors.brand_id }}</p>
              </div>

              <!-- Category -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Categoría *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-tags text-gray-400" />
                  </div>
                  <select 
                    v-model="form.product_category_id"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.product_category_id}"
                  >
                    <option value="">Seleccionar categoría</option>
                    <option v-for="category in productCategories" :key="category.id" :value="category.id">
                      {{ category.name }}
                    </option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.product_category_id" class="text-red-500 text-xs mt-1">{{ form.errors.product_category_id }}</p>
              </div>

              <!-- Contable -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">¿Es contable? *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="fa fa-calculator text-gray-400" />
                  </div>
                  <select 
                    v-model="form.contable"
                    class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 appearance-none transition-colors text-sm"
                    :class="{'border-red-300': form.errors.contable}"
                  >
                    <option value="1">Sí</option>
                    <option value="0">No</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
                <p v-if="form.errors.contable" class="text-red-500 text-xs mt-1">{{ form.errors.contable }}</p>
              </div>

              <file-input
                v-model="form.photo"
                :error="form.errors.photo"
                type="file"
                accept="image/*"
                label="Foto del producto"
              />
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-between px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button
            type="button"
            class="px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 font-medium rounded-lg transition-colors text-sm flex items-center gap-2"
            @click="destroy"
          >
            <i class="fa fa-trash" />
            Eliminar
          </button>
          
          <loading-button
            :loading="form.processing"
            class="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            type="submit"
          >
            <i class="fa fa-save" />
            Actualizar producto
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import FileInput from '@/Shared/FileInput'
import RecipeBuilder from './Partials/RecipeBuilder.vue'

export default {
  name: 'ProductEdit',

  components: {
    Head,
    Link,
    LoadingButton,
    FileInput,
    RecipeBuilder,
  },

  layout: Layout,

  remember: 'form',

  props: {
    product: Object,
    groups: Object,
    brands: Object,
    parts: Object,
    productCategories: Object,
    insumos: {
      type: Array,
      default: () => [],
    },
    availableIngredients: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        code: this.product.code,
        id: this.product.id,
        name: this.product.name,
        line_name: this.product.line_name,
        description: this.product.description,
        volumen: this.product.volumen,
        weight: this.product.weight,
        prices: [
          this.product?.prices[0]?.price,
        ],
        brand_id: this.product.brand_id,
        part_id: this.product.part_id,
        alias: this.product.alias,
        contable: this.product.contable,
        cost: this.product.cost,
        photo: null,
        product_category_id: this.product.product_category_id,
        type_product: this.product.type_product || 'product',
        recipe_items: (this.product.recipe_items || []).map(item => ({
          ingredient_id: item.ingredient_id,
          quantity: Number(item.quantity) || 1,
          unit: item.unit || '',
          cost: item.ingredient ? Number(item.ingredient.cost || 0) : 0,
        })),
      }),
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    onRecipeCostChanged(cost) {
      if (this.form.type_product === 'recipe') {
        this.form.cost = Number(cost).toFixed(2)
      }
    },

    update() {
      if (this.form.type_product === 'recipe') {
        if (!this.form.recipe_items || !this.form.recipe_items.length) {
          alert('Debes agregar al menos un insumo o ingrediente a la receta.')
          return
        }
      }

      if (this.form.type_product === 'insumo') {
        // Para insumos, si los precios de venta son 0, igualar al costo para evitar errores
        for (let i = 0; i < this.form.prices.length; i++) {
          if (Number(this.form.prices[i]) <= 0) {
            this.form.prices[i] = Number(this.form.cost) || 0.01
          }
        }
      }

      if (this.form.type_product !== 'insumo') {
        for (let i = 0; i < this.form.prices.length; i++) {
          const price = Number(this.form.prices[i])
          if (price < 0.01) {
            alert(`El precio para el grupo ${this.groups[i]?.name || (i + 1)} debe ser mayor o igual a 0.01.`)
            return
          }
          if (price < Number(this.form.cost)) {
            alert(`El precio para el grupo ${this.groups[i]?.name || (i + 1)} ($${price}) no puede ser menor al costo ($${this.form.cost}).`)
            return
          }
        }
      }

      this.form.post(`/products/${this.form.id}`)
    },

    destroy() {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.$inertia.delete(`/products/${this.product.id}`)
      }
    },
  },
}
</script>
